import {Component, inject, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, Router, RouterLink, UrlTree} from "@angular/router";
import {ButtonComponent} from "../../../shared-module/components/button/button.component";
import {InputComponent} from "../../../shared-module/components/input/input.component";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {AuthService} from "../../../shared-module/services/auth.service";
import LoaderService from "../../../shared-module/services/loader.service";
import {FormChangeListenerService} from "../../../shared-module/services/form-change-listener.service";
import {LoginResult} from "../../../shared-module/interfaces/Response.interface";
import DataResponse from "../../../shared-module/services/DataResponse";

@Component({
    selector: 'app-login',
    imports: [
    RouterLink,
    ButtonComponent,
    InputComponent,
    ReactiveFormsModule
],
    templateUrl: './login.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit {
  private readonly router: Router = inject(Router);
  private readonly authService: AuthService = inject(AuthService);
  private readonly loadingService: LoaderService = inject(LoaderService);
  private readonly formChangeListenerService: FormChangeListenerService = inject(FormChangeListenerService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);

  protected readonly document: Document = document;
  protected isLoading: boolean = false;
  protected redirectUrl: string | null = null;

  public form: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required])
  });

  constructor() {
    this.formChangeListenerService.initializeFormListener(this.form);
  }

  public ngOnInit(): void {
    this.redirectUrl = this.route.snapshot.queryParamMap.get('redirect-url');
    const urlToken: string | null = this.extractUrlToken(this.redirectUrl);
    if (urlToken) {
      this.handleTokenLogin(urlToken);
      return;
    }
    this.authService.isLoggedIn().subscribe((isLoggedIn: boolean) => {
      if (isLoggedIn) {
        this.router.navigate(["/dashboard"]);
      }
    });
  }

  public handleSubmit(): void {
    if (this.isLoading) {
      //prevents double loading screen
      return;
    }
    // Handle form submission
    if (this.form.controls['email'].hasError('email')) {
      this.form.setErrors({email: true});
      return;
    }
    if (this.form.invalid) {
      this.form.setErrors(
        {
          invalid: true
        }
      );
      this.form.updateValueAndValidity();
      return;
    }

    this.isLoading = true;
    this.loadingService.show();
    // Handle form submission
    const loginData: DataResponse<{ email: string; password: string }> = DataResponse.fromObject({
      email: this.form.value.email,
      password: this.form.value.password,
    });
    this.authService.login('/login', loginData)
      .subscribe((isLoggedIn: LoginResult) => {
        this.loadingService.hide();
        this.isLoading = false;

        if (isLoggedIn.status) {
          this.handleRedirect()
          return;
        }

        if (!isLoggedIn.status && isLoggedIn.error?.status !== 406) {
          if (isLoggedIn.error?.status === 401) {
            this.form.setErrors({invalid: true});
            return;
          }

          if (isLoggedIn.error?.status === 429) {
            this.form.setErrors({too_many_attempts: true});
            return;
          }

          this.form.setErrors({server_error: true});
          return;
        }

        const passwordTokens: {
          token: string;
        }[] | undefined = isLoggedIn?.error?.error?.data?.password_reset_tokens;

        if (passwordTokens && passwordTokens.length > 0) {
          this.router.navigate(["/login/change-password"], {
            queryParams: {
              token: passwordTokens[0].token,
              email: this.form.value.email,
              reason: 'new_password_required'
            }
          });
        }
      });
  }

  private handleRedirect(): void {
    this.router.navigate(['/login/choose-relation'], {
      queryParams: {'redirect-url': this.redirectUrl}
    });
  }

  private extractUrlToken(redirectUrl: string | null): string | null {
    if (!redirectUrl) {
      return null;
    }
    const urlTree: UrlTree = this.router.parseUrl(redirectUrl);
    return urlTree.queryParams['h'] || null;
  }

  public handleTokenLogin(token: string): void {
    if (this.isLoading) return;

    this.isLoading = true;
    this.loadingService.show();

    const loginData: DataResponse<{token: string;}> = DataResponse.fromObject({
      token: token
    });
    this.authService.login('/token', loginData)
      .subscribe({
        next: (isLoggedIn: LoginResult) => {
          this.loadingService.hide();
          this.isLoading = false;

          if (isLoggedIn.status) {
            this.handleRedirect();
            return;
          }


          if (isLoggedIn.error?.status === 401) {
            this.form.setErrors({invalid_token: true});

            if (this.redirectUrl) {
              // If the token is invalid, remove it from the URL so the user can log in normally.
              // The redirect URL is still preserved, and the invalid token message is shown only once.
              const urlTree: UrlTree = this.router.parseUrl(this.redirectUrl);
              delete urlTree.queryParams['h'];

              this.router.navigate([], {
                queryParams: {'redirect-url': urlTree.toString()},
                queryParamsHandling: 'merge'
              });
            }
            return;
          }

          this.form.setErrors({server_error: true});
          return;
        }
      });
  }
}
